/**
 * @file orderMutation.service.ts
 * @description Dedicated service handling order state transitions and kitchen modifications.
 * 
 * ARCHITECTURAL RESPONSIBILITIES:
 * 1. Progresses order status (NEW -> PREPARING -> READY -> SERVED).
 * 2. Toggles priority (Normal -> Rush / VIP).
 * 3. Performs atomic item cancellations with audit reasons via backend RPC.
 */

import { supabase } from '../../../config/supabase.config';
import type { KitchenOrderStatus, KitchenPriority } from '../../../types/order.types';

// Map chef domain status to database schema status
const mapToDbStatus = (status: KitchenOrderStatus): string => {
  if (status === 'new') return 'pending';
  if (status === 'preparing') return 'preparing';
  if (status === 'ready') return 'ready';
  if (status === 'done') return 'completed';
  if (status === 'cancelled') return 'cancelled';
  return 'pending';
};

/**
 * Transitions an order to its next operational stage in the kitchen.
 * Note: Only waiters can confirm when an order is served to the table ('completed').
 */
export const updateKitchenOrderStatus = async (
  orderId: string,
  newStatus: KitchenOrderStatus
): Promise<void> => {
  const dbStatus = mapToDbStatus(newStatus);
  const { data: { user } } = await supabase.auth.getUser();

  // Safely persist kitchen attribution and pass timestamp inside order metadata
  const { data: currentOrder } = await supabase
    .from('orders')
    .select('metadata')
    .eq('id', orderId)
    .maybeSingle();

  const currentMeta = (currentOrder?.metadata as Record<string, any>) || {};
  const newMeta = { ...currentMeta };

  if (user?.id) {
    newMeta.chef_id = user.id;
  }

  if (newStatus === 'ready') {
    newMeta.ready_at = new Date().toISOString();
  }

  const updates: Record<string, any> = {
    status: dbStatus,
    metadata: newMeta,
  };

  const { error } = await supabase
    .from('orders')
    .update(updates)
    .eq('id', orderId);

  if (error) {
    console.error('[orderMutation.service] Status update error:', error);
    throw error;
  }
};

/**
 * Updates order priority tag (Normal, Rush, VIP).
 */
export const setKitchenOrderPriority = async (
  orderId: string,
  priority: KitchenPriority
): Promise<void> => {
  const { error } = await supabase
    .from('orders')
    .update({ priority })
    .eq('id', orderId);

  if (error) {
    console.error('[orderMutation.service] Priority update error:', error);
    throw error;
  }
};

/**
 * Cancels a single order item with a mandatory audit reason.
 */
export const cancelOrderItemWithReason = async (
  orderItemId: string,
  reason: string
): Promise<void> => {
  const { error } = await supabase.rpc('cancel_order_item_with_reason', {
    p_order_item_id: orderItemId,
    p_reason: reason,
  });

  if (error) {
    console.error('[orderMutation.service] Cancel item error:', error);
    throw error;
  }
};
